import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

const FIXED_CLASS = 'lyckd-scroll-header--fixed';
const VISIBLE_CLASS = 'lyckd-scroll-header--visible';
const INSTANT_CLASS = 'lyckd-scroll-header--instant';
const DIRECTION_THRESHOLD = 8;
const HIDE_TRANSITION_DURATION = 500;

function initializeScrollHeader() {
  window.lyckdScrollHeaderController?.destroy();

  const headerGroup = document.querySelector('#header-group');
  if (!(headerGroup instanceof HTMLElement)) return;

  let scrollTarget = getScrollEventTarget();
  let previousScrollTop = getScrollTop();
  let naturalHeaderHeight = headerGroup.offsetHeight;
  let animationFrame = null;
  let revealFrame = null;
  let hideLockTimer = null;
  let isHiding = false;
  let scrollDirection = 0;
  let directionalDistance = 0;

  const clearRevealFrame = () => {
    if (revealFrame === null) return;
    window.cancelAnimationFrame(revealFrame);
    revealFrame = null;
  };

  const clearHideLock = () => {
    if (hideLockTimer !== null) window.clearTimeout(hideLockTimer);
    hideLockTimer = null;
    isHiding = false;
  };

  const lockUntilHidden = () => {
    clearHideLock();
    isHiding = true;
    hideLockTimer = window.setTimeout(() => {
      hideLockTimer = null;
      isHiding = false;
      scrollDirection = 0;
      directionalDistance = 0;
    }, HIDE_TRANSITION_DURATION);
  };

  const resetToNaturalPosition = () => {
    clearRevealFrame();
    clearHideLock();
    headerGroup.classList.remove(VISIBLE_CLASS, FIXED_CLASS, INSTANT_CLASS);
  };

  const reveal = () => {
    // Momentum scrolling can briefly report the opposite direction while the
    // header is leaving. Let that one hide finish instead of reversing it.
    if (isHiding) return;

    if (!headerGroup.classList.contains(FIXED_CLASS)) {
      headerGroup.classList.add(FIXED_CLASS);
      clearRevealFrame();
      revealFrame = window.requestAnimationFrame(() => {
        revealFrame = null;
        headerGroup.classList.add(VISIBLE_CLASS);
      });
      return;
    }

    if (revealFrame !== null) return;
    headerGroup.classList.add(VISIBLE_CLASS);
  };

  const conceal = () => {
    const wasFixed = headerGroup.classList.contains(FIXED_CLASS);
    const wasVisible = headerGroup.classList.contains(VISIBLE_CLASS);
    clearRevealFrame();

    // The natural header is already above the viewport at this point. Switch
    // it into its hidden sticky state without animating through the viewport.
    if (!wasFixed) {
      headerGroup.classList.add(INSTANT_CLASS, FIXED_CLASS);
      headerGroup.classList.remove(VISIBLE_CLASS);
      void headerGroup.offsetHeight;
      headerGroup.classList.remove(INSTANT_CLASS);
      return;
    }

    headerGroup.classList.add(FIXED_CLASS);
    headerGroup.classList.remove(VISIBLE_CLASS);
    if (wasVisible && !isHiding) lockUntilHidden();
  };

  const update = () => {
    animationFrame = null;
    const currentScrollTop = getScrollTop();

    // Preserve the currently visible header and marquee while the mobile
    // navigation owns the viewport and page scrolling is locked.
    if (document.querySelector('.lyckd-mobile-nav__details[open]')) {
      previousScrollTop = currentScrollTop;
      scrollDirection = 0;
      directionalDistance = 0;
      return;
    }

    const scrollDelta = currentScrollTop - previousScrollTop;
    const nextDirection = scrollDelta > 0 ? 1 : scrollDelta < 0 ? -1 : 0;

    if (nextDirection !== 0) {
      if (nextDirection !== scrollDirection) {
        scrollDirection = nextDirection;
        directionalDistance = Math.abs(scrollDelta);
      } else {
        directionalDistance += Math.abs(scrollDelta);
      }
    }

    if (currentScrollTop <= 1) {
      resetToNaturalPosition();
      scrollDirection = 0;
      directionalDistance = 0;
    } else if (
      (headerGroup.classList.contains(FIXED_CLASS) || currentScrollTop > naturalHeaderHeight) &&
      directionalDistance >= DIRECTION_THRESHOLD
    ) {
      if (scrollDirection < 0) {
        reveal();
      } else {
        conceal();
      }
      directionalDistance = 0;
    }

    previousScrollTop = currentScrollTop;
  };

  const onScroll = () => {
    if (animationFrame !== null) return;
    animationFrame = window.requestAnimationFrame(update);
  };

  const bindScrollTarget = () => {
    scrollTarget.removeEventListener('scroll', onScroll);
    scrollTarget = getScrollEventTarget();
    previousScrollTop = getScrollTop();
    scrollDirection = 0;
    directionalDistance = 0;
    if (previousScrollTop > naturalHeaderHeight) {
      headerGroup.classList.add(FIXED_CLASS);
    }
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
  };

  const resizeObserver = new ResizeObserver(() => {
    if (!headerGroup.classList.contains(FIXED_CLASS)) {
      naturalHeaderHeight = headerGroup.offsetHeight;
    }
  });

  resizeObserver.observe(headerGroup);
  scrollTarget.addEventListener('scroll', onScroll, { passive: true });
  scrollContainerMediaQuery.addEventListener('change', bindScrollTarget);

  if (previousScrollTop > naturalHeaderHeight) {
    headerGroup.classList.add(FIXED_CLASS);
  }

  window.lyckdScrollHeaderController = {
    destroy() {
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      clearRevealFrame();
      clearHideLock();
      resizeObserver.disconnect();
      scrollTarget.removeEventListener('scroll', onScroll);
      scrollContainerMediaQuery.removeEventListener('change', bindScrollTarget);
      resetToNaturalPosition();
    },
  };
}

initializeScrollHeader();
document.addEventListener('shopify:section:load', initializeScrollHeader);
